import {
  ActiveCase,
  AircraftCardItem,
  AircraftEventLegacy,
  AircraftList,
  AircraftStatus,
  type AircraftOnMap,
} from '#entities';
import {
  EAircraftEventStatus,
  EAircraftStatusType,
  ECaseCardStatus,
  ENOPOrganType,
} from '#shared';

import { getCurrentEventLegacy, getEventTime } from './aircrafts';

const findActiveCase = (
  cases: ActiveCase[],
  item: AircraftEventLegacy,
): ActiveCase | null => {
  if (item.Type === EAircraftEventStatus.OnTrip && item.Label) {
    return (
      cases.find(i => {
        const externalReference = item.Label?.toLowerCase() ?? '';
        const unosId = i.label.substring(0, 7);
        const organFull = i.organ.name;
        const organShort =
          organFull === ENOPOrganType.Heart
            ? 'ht|hr'
            : organFull.substring(0, 2).toLocaleLowerCase();

        if (externalReference.includes(unosId.toLowerCase())) {
          const reference = externalReference.split(unosId.toLowerCase())[1];

          const organFullRegex = new RegExp(organFull.toLowerCase(), 'gm');
          const organShortRegex = new RegExp(organShort, 'gm');

          return (
            !!RegExp(organFullRegex).exec(reference) ||
            !!RegExp(organShortRegex).exec(reference)
          );
        }

        return false;
      }) || null
    );
  }

  return null;
};

// consider to remove
export const transformAircrafts = (
  data: AircraftStatus[],
  cases: ActiveCase[] | null = null,
): AircraftList =>
  data.reduce<AircraftList>((acc, item) => {
    const currentEvent = getCurrentEventLegacy(item.Events);

    if (item.Type === EAircraftStatusType.OnMaintenance) {
      const onDutyTime =
        currentEvent?.Type === EAircraftEventStatus.OnDuty
          ? getEventTime(currentEvent.EventTimeMs)
          : 0;

      return [
        ...acc,
        {
          Id: `aircraft-${item.Id}`,
          Registration: item.Registration,
          Airport: item.Airport,
          TimeOnDuty: onDutyTime,
          Type: EAircraftStatusType.OnMaintenance,
          MaintenanceType: currentEvent.MaintenanceType,
          MaintenanceName: currentEvent.MaintenanceName,
          isActual: currentEvent.isActual,
        } as unknown as AircraftOnMap,
      ];
    }

    if (
      item.Type === EAircraftStatusType.OnGround &&
      item.Location?.Longitude &&
      item.Location?.Latitude
    ) {
      const onDutyTime =
        currentEvent?.Type === EAircraftEventStatus.OnDuty
          ? getEventTime(currentEvent.EventTimeMs)
          : 0;

      return [
        ...acc,
        {
          Id: `aircraft-${item.Id}`,
          Registration: item.Registration,
          Location: item.Location,
          Airport: item.Airport,
          TimeOnDuty: onDutyTime,
          Type: EAircraftStatusType.OnGround,
        } as unknown as AircraftOnMap,
      ];
    }

    if (
      item.Type === EAircraftStatusType.OnFly &&
      item.Location?.Latitude &&
      item.Location?.Longitude
    ) {
      const showOnFlyAircraft = !!cases || item.IsReturnToBase;
      const currentCase =
        item.IsOnCase && cases ? findActiveCase(cases, currentEvent) : null;

      if (showOnFlyAircraft) {
        return [
          ...acc,
          {
            Id: `aircraft-${item.Id}`,
            Registration: item.Registration,
            IsOnFly: item.IsOnFly,
            Location: item.Location,
            Direction: item.Direction,
            Airports: item.Airports,
            IsReturnToBase: item.IsReturnToBase,
            TripLegStart: item.TripLegStart,
            TripLegEnd: item.TripLegEnd,
            IsOnCase: item.IsOnCase,
            Label: currentCase?.label,
            Organ: currentCase?.organ.name,
            IsLate: currentCase?.cardStatus === ECaseCardStatus.Warning,
            Type: item.Type,
          } as AircraftOnMap,
        ];
      }
    }

    return acc;
  }, []);

export const setCasesToAircrafts = (
  aircrafts: AircraftStatus[],
  cases: ActiveCase[],
): AircraftCardItem[] =>
  aircrafts
    .map<AircraftCardItem>(item => {
      const currentEvents = item.Events.map(item => {
        if (item.Type === EAircraftEventStatus.OnTrip) {
          const currentCase = findActiveCase(cases, item);

          if (currentCase) {
            return {
              ...item,
              Label: currentCase.label,
              Organ: currentCase.organ,
              IsLate: currentCase.cardStatus === ECaseCardStatus.Warning,
            };
          }
        }

        return item;
      });

      return {
        ...item,
        Events: currentEvents,
      };
    })
    .sort((a, b) => {
      if (a.Type === 'OnMaintenance') {
        return 1;
      }
      if (b.Type === 'OnMaintenance') {
        return -1;
      }
      if (a.Id > b.Id) {
        return 1;
      }
      return -1;
    });
